import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { REPLENISHMENT_API_TOKEN } from '@epicuro-next/data/cmi';
import { DOCUMENT_CONTROL_API_TOKEN } from '@epicuro-next/data/engagement/document-control';
import { ENGAGEMENT_API_TOKEN } from '@epicuro-next/data/engagement/engagement-models';
import { ORDERS_V2_API_TOKEN } from '@epicuro-next/data/order-book';
import {
  PRODUCTS_SEARCH_API_TOKEN,
  SIZE_RUNS_API_TOKEN,
} from '@epicuro-next/data/product';
import { REPORTING_API_TOKEN } from '@epicuro-next/data/reporting-api';
import { AuthenticationService } from '@epicuro-next/platform/authentication';
import { selectIsSearchV2Enabled } from '@epicuro-next/platform/configuration';
import { RULE_ENGINE_V2_API } from '@epicuro-next/platform/rule-engine';
import { selectConnectToken } from '@epicuro-next/state/connect-token-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Store } from '@ngrx/store';
import { MEDIA_RELATIONS_API_TOKEN } from 'data-media-relations';
import { filter, switchMap, take, withLatestFrom } from 'rxjs/operators';

interface GqlBody {
  query: string;
  operationName: string;
  variables: {
    query: Record<string, string>;
  };
}

export const connectBearerInterceptor: HttpInterceptorFn = (
  request,
  handle,
) => {
  const store = inject(Store);
  const authService = inject(AuthenticationService);
  const connectToken = store.select(selectConnectToken);
  const v2AuthEndpoints = [
    inject(ENGAGEMENT_API_TOKEN),
    inject(SIZE_RUNS_API_TOKEN),
    inject(PRODUCTS_SEARCH_API_TOKEN),
    inject(REPLENISHMENT_API_TOKEN),
    inject(ORDERS_V2_API_TOKEN),
    inject(RULE_ENGINE_V2_API),
    inject(REPORTING_API_TOKEN),
    inject(DOCUMENT_CONTROL_API_TOKEN),
    inject(MEDIA_RELATIONS_API_TOKEN),
    '/api/v2/recommendation/suggest',
  ];

  const isV2Auth = v2AuthEndpoints.some((url) => request.url.startsWith(url));
  const isV2GqlAuth = isV2GqlAuthRequest(request);

  if (!isV2Auth && !isV2GqlAuth) {
    return handle(request);
  }

  return connectToken.pipe(
    filter(isNonNullish),
    take(1),
    withLatestFrom(store.select(selectIsSearchV2Enabled)),
    switchMap(([token, isSearchV2Enabled]: [string, boolean]) => {
      if (!token) {
        return handle(request);
      }

      if (isV2GqlAuth && !applyV2GqlRequest(request, isSearchV2Enabled)) {
        return handle(request);
      }

      const accessToken = authService.accessToken;
      const headers = request.headers
        .set('Authorization', `Bearer ${token}`) // override existing auth token with connect token
        .append('Epc-Token', `Bearer ${accessToken}`);

      request = request.clone({
        headers,
      });

      return handle(request);
    }),
  );
};

const STATIC_V2_GQL_AUTH_OPERATIONS = ['searchSuggest'].map((v) =>
  v.toLowerCase(),
);

const RULE_BASED_V2_GQL_AUTH_OPERATIONS = ['search', 'filters'].map((v) =>
  v.toLowerCase(),
);

function isV2GqlAuthRequest(request: HttpRequest<unknown>): boolean {
  if (request.url.includes('/api/v2/gql/')) {
    const body = request.body as GqlBody;

    if (!body) {
      return false;
    }
    const operationName = body?.operationName?.toLowerCase();

    return [
      ...STATIC_V2_GQL_AUTH_OPERATIONS,
      ...RULE_BASED_V2_GQL_AUTH_OPERATIONS,
    ].includes(operationName);
  }
  return false;
}

// Additional check after pass isV2GqlAuthRequest for rule base requests
function applyV2GqlRequest(
  request: HttpRequest<unknown>,
  ruleEnabled: boolean,
): boolean {
  const body = request.body as GqlBody;

  if (!body) {
    return false;
  }
  const operationName = body.operationName?.toLowerCase();

  if (STATIC_V2_GQL_AUTH_OPERATIONS.includes(operationName)) {
    return true;
  }

  if (!ruleEnabled) {
    return false;
  }

  // use only when we have useV2Search in query (because it is used for p-basket needs only for now)
  return !!body?.variables?.query?.useV2Search;
}
